

import React, { useEffect, useRef, useState } from "react";
import { HiFilter, HiPlus, HiSearch } from "react-icons/hi";
import { TbFileTypePdf } from "react-icons/tb";
import UsersTable from "./UsersTable";
import CardWidget from "./CardWidget";
import { getAllUser } from "Common Usage/apiService";

const AllTraficUsers = ({ searchTerm, setSearchTerm, page, setPage }) => {
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [isOptionDropdownOpen, setIsOptionDropdownOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("Language Wise");

  const [filter, setFilter] = useState("All Users");
  const [selectedLanguageOption, setSelectedLanguageOption] = useState("select-language");
  const [selectedLocationOption, setSelectedLocationOption] = useState("select-location");

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  const filterDropdownRef = useRef(null);
  const optionDropdownRef = useRef(null);

  const [counts, setCounts] = useState({ maleUsers: 0, femaleUsers: 0, idleUsers: 0 });

  // close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setIsFilterDropdownOpen(false);
      }
      if (optionDropdownRef.current && !optionDropdownRef.current.contains(event.target)) {
        setIsOptionDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // reset filters when tab changes
  useEffect(() => {
    if (activeTab === "Language Wise") setSelectedLanguageOption("select-language");
    else setSelectedLocationOption("select-location");
    setPage(1);
  }, [activeTab, setPage]);

  // fetch users + counts
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const preferred_Language =
          activeTab === "Language Wise" &&
          selectedLanguageOption !== "All Language" &&
          selectedLanguageOption !== "select-language"
            ? selectedLanguageOption
            : undefined;

        const data = await getAllUser(page, searchTerm, filter, 5, preferred_Language);
        setUsers(data.data);
        setTotalPages(data.totalPages);
        setCounts(data.counts || { maleUsers: 0, femaleUsers: 0, idleUsers: 0 });
      } catch (error) {
        console.log("fetchAllUsers-Error", error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [searchTerm, filter, page, selectedLanguageOption, activeTab]);

  const commonFilters = ["All Users", "Male Users", "Female Users", "Online Users", "Offline Users"];

  const getOptionFilters = () => {
    if (activeTab === "Language Wise") return ["All Language", "Odia", "English", "Hindi", "Bengali", "Tamil"];
    if (activeTab === "Location Wise") return ["Odisha", "Delhi", "Mumbai", "Chennai"];
    return [];
  };

  return (
    <div className="py-5">
      {/* Tabs */}
      <div className="hidden sm:flex">
        <button
          className={`py-2 px-16 text-lg rounded-tl-2xl ${
            activeTab === "Language Wise"
              ? "bg-gradient-to-br from-[#f8b7d5] to-white font-semibold"
              : "bg-white"
          }`}
          onClick={() => setActiveTab("Language Wise")}
        >
          Language Wise
        </button>
        <button
          className={`py-2 px-16 text-lg rounded-tr-2xl ${
            activeTab === "Location Wise"
              ? "bg-gradient-to-bl from-[#f8b7d5] to-white font-semibold"
              : "bg-white"
          }`}
          onClick={() => setActiveTab("Location Wise")}
        >
          Location Wise
        </button>
      </div>

      {/* Top bar */}
      <div className="bg-white shadow-sm sm:rounded-2xl p-5 flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
        {/* Filter Dropdown */}
        <div className="relative w-full md:w-1/5" ref={filterDropdownRef}>
          <button
            className="flex items-center py-2 px-4 border rounded-md bg-white hover:bg-gray-100 w-full"
            onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
          >
            <HiFilter color="#c51369" className="h-5 w-5 mr-2" />
            {filter}
          </button>
          {isFilterDropdownOpen && (
            <div className="absolute left-0 mt-2 w-full bg-white border rounded-md shadow-lg z-10">
              <ul className="py-2">
                {commonFilters.map((option) => (
                  <li
                    key={option}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      setFilter(option);
                      setPage(1);
                      setSearchTerm("");
                      setIsFilterDropdownOpen(false);
                    }}
                  >
                    {option}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Option Dropdown (Language/Location) */}
        <div className="relative w-full md:w-1/5" ref={optionDropdownRef}>
          <button
            className="flex items-center py-2 px-2 border rounded-md bg-white hover:bg-gray-100 w-full"
            onClick={() => setIsOptionDropdownOpen(!isOptionDropdownOpen)}
          >
            <HiFilter color="#c51369" className="h-5 w-5 mr-1" />
            {activeTab === "Language Wise" ? selectedLanguageOption : selectedLocationOption}
          </button>
          {isOptionDropdownOpen && (
            <div className="absolute left-0 mt-2 w-full bg-white border rounded-md shadow-lg z-10">
              <ul className="py-2">
                {getOptionFilters().map((option) => (
                  <li
                    key={option}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      if (activeTab === "Language Wise") setSelectedLanguageOption(option);
                      else setSelectedLocationOption(option);
                      setPage(1);
                      setIsOptionDropdownOpen(false);
                    }}
                  >
                    {option}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="w-full md:w-1/3">
          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center">
              <HiSearch color="#c51369" className="h-5 w-5" />
            </span>
            <input
              type="text"
              placeholder="Search with nickname and phone..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
                if (filter !== "All Users") setFilter("All Users");
              }}
              className="block w-full pl-10 py-2 px-4 border rounded-md focus:ring-1 focus:ring-[#c51369]"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-4">
          <button className="p-3 bg-[#c51369] hover:bg-[#a10f5b] text-white rounded-full">
            <TbFileTypePdf className="h-5 w-5" />
          </button>
          <button className="p-3 bg-[#c51369] hover:bg-[#a10f5b] text-white rounded-full">
            <HiPlus className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Widgets + Table */}
      <CardWidget 
        male={counts?.maleUsers || 0} 
        female={counts?.femaleUsers || 0} 
        idle={counts?.idleUsers || 0}
        selectedLanguage={selectedLanguageOption} 
      />

      <UsersTable
        users={users}
        loading={loading}
        page={page}
        setPage={setPage}
        totalPages={totalPages}
      />
    </div>
  );
};

export default AllTraficUsers;
